import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Alert, Box, Button, TextField,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface LogEntry {
  id: number;
  tenant_id: number | null;
  user_id: number | null;
  agent_id: string | null;
  event_type: string;
  event_detail: string | null;
  ip_address: string | null;
  created_at: string | null;
}

const EVENT_TYPES = [
  '', 'login', 'login_failed', 'password_change', 'config_change',
  'agent_connect', 'agent_disconnect', 'alert_received', 'alert_delivered',
  'alert_failed', 'tone_upload', 'tone_delete', 'pronunciation_update',
];

export default function SystemLogPage() {
  const { tenantId } = useAuth();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventFilter, setEventFilter] = useState('');
  const [limit, setLimit] = useState(100);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      let url = `/api/tenants/${tenantId}/system-log?limit=${limit}`;
      if (eventFilter) url += `&event_type=${encodeURIComponent(eventFilter)}`;
      const res = await api.get(url);
      setLogs(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load system log');
    } finally {
      setLoading(false);
    }
  }, [tenantId, eventFilter, limit]);

  useEffect(() => { load(); }, [load]);

  const formatTime = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">System Log</Typography>
        <Button startIcon={<Refresh />} onClick={load} variant="outlined">Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Event Type</InputLabel>
          <Select value={eventFilter} label="Event Type" onChange={e => setEventFilter(e.target.value)} size="small">
            <MenuItem value="">All Events</MenuItem>
            {EVENT_TYPES.filter(Boolean).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Limit</InputLabel>
          <Select value={limit} label="Limit" onChange={e => setLimit(e.target.value as number)} size="small">
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={250}>250</MenuItem>
            <MenuItem value={500}>500</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Event Type</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>Agent ID</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center">Loading...</TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">No log entries</TableCell></TableRow>
            ) : logs.map(l => (
              <TableRow key={l.id}>
                <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{formatTime(l.created_at)}</TableCell>
                <TableCell><code>{l.event_type}</code></TableCell>
                <TableCell>{l.user_id ?? '—'}</TableCell>
                <TableCell>{l.agent_id || '—'}</TableCell>
                <TableCell>{l.ip_address || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.event_detail || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
